import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogField, DialogFooter, dialogInputStyle } from "@/components/ui/Dialog";
import { t } from "@/lib/strings";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Suggested name for the new group (e.g. "<Quelle> Kopie"). */
  defaultName: string;
  onConfirm: (name: string, withComments: boolean) => void;
  pending?: boolean;
}

/** Names the copy when duplicating a whole group ("Neue Aktion"-style clone). */
export function DuplicateGroupDialog({
  open,
  onOpenChange,
  defaultName,
  onConfirm,
  pending,
}: Props) {
  const nameId = useId();
  const [name, setName] = useState(defaultName);
  const [withComments, setWithComments] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setWithComments(false);
    }
  }, [open, defaultName]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !pending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    onConfirm(trimmed, withComments);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.groupDuplicateTitle}
      description={t.groupDuplicateDescription}
    >
      <form onSubmit={onSubmit}>
        <DialogField label={t.groupDuplicateNameLabel} htmlFor={nameId}>
          <input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={255}
            style={dialogInputStyle}
          />
        </DialogField>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 12,
            fontSize: 13,
            color: "var(--ink-2)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={withComments}
            onChange={(e) => setWithComments(e.target.checked)}
          />
          {t.copyIncludeComments}
        </label>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t.cancel}
          </Button>
          <Button type="submit" variant="primary" size="md" disabled={!canSubmit}>
            {pending ? t.saving : t.groupDuplicateConfirm}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
